import { render, scene } from '@/threejs/core';

/**
 * ProjectorManager - Centralised management for projector visibility settings
 * Handles showing/hiding projector labels, frustums, and sprites across all projectors
 */
class ProjectorManager {
  constructor() {
    this.isInitialized = false;
    this.settings = {
      showProjectorInfo: false, // Labels (distance, pixel size, etc.)
      showProjectorFrustums: false, // Helper lines/frustums
      showProjectorSprite: false, // Projector and target sprites
    };
  }

  /**
   * Initialize the manager
   */
  initialize() {
    this.isInitialized = true;
  }

  /**
   * Check if manager is initialized
   * @returns {boolean}
   */
  isManagerInitialized() {
    return this.isInitialized;
  }

  /**
   * Get all projector objects from the scene
   * @returns {Array} Array of projector objects with projectorHelpers
   */
  getAllProjectors() {
    const projectors = [];
    scene.traverse((object) => {
      if (object.userData.type === 'projector' && object.projectorHelpers) {
        projectors.push(object);
      }
    });
    return projectors;
  }

  /**
   * Handle projector-related viewport settings
   * @param {string} key - Setting key (e.g., 'render.showProjectorInfo')
   * @param {boolean} value - Setting value
   */
  handleProjectorSetting(key, value) {
    if (!this.isInitialized) {
      this.initialize();
    }

    switch (key) {
      case 'render.showProjectorInfo':
        this.setProjectorInfoVisible(value);
        break;
      case 'render.showProjectorFrustums':
        this.setProjectorFrustumsVisible(value);
        break;
      case 'render.showProjectorSprite':
        this.setProjectorSpritesVisible(value);
        break;
      default:
        console.warn(`Unhandled projector setting: ${key}`);
    }
  }

  /**
   * Show/hide projector info labels (distance, pixel size, dimensions)
   * @param {boolean} visible - Whether labels should be visible
   */
  setProjectorInfoVisible(visible) {
    this.settings.showProjectorInfo = visible;
    const projectors = this.getAllProjectors();

    projectors.forEach((projector) => {
      if (projector.projectorHelpers?.updateLabelVisibility) {
        projector.projectorHelpers.updateLabelVisibility(visible);
      }
    });

    render();
  }

  /**
   * Show/hide projector frustums (helper lines showing projection area)
   * @param {boolean} visible - Whether frustums should be visible
   */
  setProjectorFrustumsVisible(visible) {
    this.settings.showProjectorFrustums = visible;
    const projectors = this.getAllProjectors();

    projectors.forEach((projector) => {
      const helpers = projector.projectorHelpers;
      if (!helpers) return;

      // Update visibility of all frustum-related helper lines
      if (helpers.helperLines) {
        helpers.helperLines.visible = visible;
      }
      if (helpers.centerLine) {
        helpers.centerLine.visible = visible;
      }
      if (helpers.frame) {
        helpers.frame.visible = visible;
      }
      if (helpers.extendedCornerLines) {
        helpers.extendedCornerLines.visible = visible;
      }
      if (helpers.projectorOutline) {
        helpers.projectorOutline.visible = visible;
      }
    });

    render();
  }

  /**
   * Show/hide projector and target sprites (clickable icons)
   * @param {boolean} visible - Whether sprites should be visible
   */
  setProjectorSpritesVisible(visible) {
    this.settings.showProjectorSprite = visible;
    const projectors = this.getAllProjectors();

    projectors.forEach((projector) => {
      if (projector.projectorHelpers) {
        // Update projector sprite visibility
        if (projector.projectorHelpers.updateProjectorSpriteVisibility) {
          projector.projectorHelpers.updateProjectorSpriteVisibility(visible);
        }
        // Update target sprite visibility
        if (projector.projectorHelpers.updateTargetSpriteVisibility) {
          projector.projectorHelpers.updateTargetSpriteVisibility(visible);
        }
      }
    });

    render();
  }

  /**
   * Get current settings
   * @returns {Object} Current projector visibility settings
   */
  getSettings() {
    return { ...this.settings };
  }

  /**
   * Apply settings to a newly added projector
   * Useful when a projector is added to the scene after initial setup
   * @param {Object} projector - The projector object with projectorHelpers
   */
  applySettingsToProjector(projector) {
    if (!projector.projectorHelpers) return;

    const { showProjectorInfo, showProjectorFrustums, showProjectorSprite } = this.settings;

    // Apply label visibility
    if (projector.projectorHelpers.updateLabelVisibility) {
      projector.projectorHelpers.updateLabelVisibility(showProjectorInfo);
    }

    // Apply frustum visibility
    const helpers = projector.projectorHelpers;
    if (helpers.helperLines) helpers.helperLines.visible = showProjectorFrustums;
    if (helpers.centerLine) helpers.centerLine.visible = showProjectorFrustums;
    if (helpers.frame) helpers.frame.visible = showProjectorFrustums;
    if (helpers.extendedCornerLines) helpers.extendedCornerLines.visible = showProjectorFrustums;
    if (helpers.projectorOutline) helpers.projectorOutline.visible = showProjectorFrustums;

    // Apply sprite visibility
    if (projector.projectorHelpers.updateProjectorSpriteVisibility) {
      projector.projectorHelpers.updateProjectorSpriteVisibility(showProjectorSprite);
    }
    if (projector.projectorHelpers.updateTargetSpriteVisibility) {
      projector.projectorHelpers.updateTargetSpriteVisibility(showProjectorSprite);
    }
  }

  /**
   * Reset the manager to initial state
   */
  reset() {
    this.isInitialized = false;
    this.settings = {
      showProjectorInfo: false,
      showProjectorFrustums: false,
      showProjectorSprite: false,
    };
  }
}

// Export singleton instance
export const projectorManager = new ProjectorManager();
